/**
 * Editar o mapa: a paleta, o plano e o painel do bloco escolhido.
 *
 * A tela guarda uma cópia da configuração e cada gesto a muda (`lib/edicao`).
 * Nada vai ao serviço antes de "Salvar rascunho", que manda a cópia inteira e vira uma
 * versão nova. Numa automação no ar, a versão nova é a alteração em teste: a
 * que está no ar continua rodando.
 *
 * O desenho segue o protótipo v2: o bloco com o ícone na cor do grupo, o
 * contador do que falta e cada saída com o ponto na cor dela; a ligação
 * pinta a cor da saída de onde sai, com a legenda no canto do plano; a
 * paleta com busca; a pílula "Nada a acertar" ou "N pendências" na barra. O
 * bloco novo entra em `--transicao-entrada`, o arrastado se levanta, e a
 * ligação em andamento sai tracejada em roxo.
 *
 * "Ver o caminho no mapa", na simulação, anda pelo mapa com o caminho que o
 * serviço devolveu: o bloco da vez ganha o contorno, a ligação usada corre, e
 * o que ficou fora do caminho apaga.
 */

import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Background,
  Panel,
  ReactFlow,
  type Connection,
  type Edge,
  type EdgeChange,
  type Node,
  type NodeChange,
  type ReactFlowInstance,
} from '@xyflow/react';
import {
  ErroDaApi,
  type AutomacaoDaApi,
  type BlocosDoEditor,
  type EditarAutomacao,
} from '@cliqcrm/contracts';
import { ATIVA_AUTOMACAO, MONTA_AUTOMACAO_COM_SQL } from '@/entities/acesso/papel';
import { chaves } from '@/shared/api/chaves';
import { useApi, useOperacoesDeDados } from '@/shared/api/consultas';
import { recadoDeErro } from '@/shared/api/recado-de-erro';
import { registrarGuardaDeSaida } from '@/shared/lib/guarda-de-saida';
import { Botao, Campo, Dialogo, Icone, Selo } from '@/shared/ui';
import { Faixa } from '@/shared/ui/estados/faixa';
import { iconeDoTipo } from '../lib/aparencia';
import { comPosicoes, desenharMapa, organizarTudo } from '../lib/desenho';
import {
  PROXIMO,
  adicionarBloco,
  adicionarDepois,
  buscarNaPaleta,
  configuracaoDe,
  desligar,
  duplicarBloco,
  errosPorBloco,
  itensDaPaleta,
  semAcento,
  ligar,
  mover,
  pendencias,
  removerBlocos,
  versaoEditada,
  type Configuracao,
  type ItemDaPaleta,
} from '../lib/edicao';
import { ehOInicioAtual, iniciosDaPaleta, trocarInicio, type InicioDaPaleta } from '../lib/inicio';
import { ID_DO_COMECO, descreverNo } from '../lib/mapa';
import { expandirMenus, idNaTela } from '../lib/menu';
import { percursoDaSimulacao, type Percurso } from '../lib/percurso';
import {
  FerramentasDoMapa,
  LegendaDoMapa,
  TIPOS_DE_BLOCO,
  TIPOS_DE_LIGACAO,
  ligacaoParaOMapa,
  movimentoReduzido,
  type DadosDaLigacao,
  type DadosDoBloco,
} from './bloco-do-mapa';
import { PainelDoBloco } from './painel-do-bloco';
import { ProximoPasso } from './proximo-passo';
import { ConversaDeTeste } from './conversa-de-teste';
import { RESULTADO_DA_SIMULACAO, Simular, type Simulacao } from './simular';
import { NomearAutomacao } from './nomear-automacao';

export function EdicaoDoMapa({
  automacao,
  inicial,
  versaoInicial,
  recemCriado = false,
  blocos,
  papel,
  aoSair,
  cabecalho,
}: {
  automacao: AutomacaoDaApi;
  inicial: Configuracao;
  /** A versão que a tela leu ao abrir. Sem nada por salvar, acompanha a automação relida. */
  versaoInicial: number;
  /** Acabou de ser criada do zero: o recado do rascunho aparece no mapa. */
  recemCriado?: boolean;
  blocos: BlocosDoEditor | undefined;
  papel: string | null;
  aoSair: () => void;
  /** O nome, a situação e o histórico: a barra é o cabeçalho da tela, que o mapa ocupa inteira. */
  cabecalho?: {
    titulo: string;
    selo: ReactNode;
    apoio?: string;
    historico?: ReactNode;
  };
}): ReactNode {
  const api = useApi();
  const cache = useQueryClient();
  const [config, definirConfig] = useState(() => comPosicoes(inicial, blocos));
  const [sujo, definirSujo] = useState(false);
  const [selecionado, definirSelecionado] = useState<string | null>(null);
  const [errosDoServico, definirErrosDoServico] = useState<Record<string, string[]>>({});
  /** Os casos de uma escolha que ainda não levam a lugar nenhum. */
  const [casosNovos, definirCasosNovos] = useState<Record<string, string[]>>({});
  /** A saída que esperou a resposta: sair (pelo voltar, pelo menu) com alteração não salva. */
  const [saidaPendente, definirSaidaPendente] = useState<(() => void) | null>(null);
  const [salvo, definirSalvo] = useState<string | null>(null);
  const [simulando, definirSimulando] = useState(false);
  /** O servidor conferiu ESTA cópia e não achou nada (some na próxima mudança). */
  const [conferido, definirConferido] = useState(false);
  const [confirmandoDescarte, definirConfirmandoDescarte] = useState(false);
  const porQueDescartar = useId();
  const [confirmandoAtivacao, definirConfirmandoAtivacao] = useState(false);
  const oQueAtivarFaz = useId();
  /*
   * A versão lida fica guardada: se a automação for buscada de novo (outra
   * aba, outra pessoa salvou) com algo por salvar aqui, salvar ainda manda a
   * que esta cópia leu, e o serviço responde que mudou, em vez de passar por
   * cima. Sem nada por salvar, ela acompanha a relida (o efeito abaixo).
   */
  const [versaoLida, definirVersaoLida] = useState(versaoInicial);
  const [ligacaoEscolhida, definirLigacaoEscolhida] = useState<string | null>(null);
  const [buscaNaPaleta, definirBuscaNaPaleta] = useState('');
  /** O bloco que acabou de entrar: ele aparece com a entrada, uma vez só. */
  const [recemPosto, definirRecemPosto] = useState<string | null>(null);
  useEffect(() => {
    if (!recemPosto) return;
    const prazo = window.setTimeout(() => definirRecemPosto(null), 600);
    return () => window.clearTimeout(prazo);
  }, [recemPosto]);
  /**
   * A simulação mostrada no mapa: o caminho que o serviço devolveu, andando um
   * bloco por vez (`atual`); em `nos.length`, terminou. Com o movimento
   * reduzido, ele aparece inteiro de uma vez.
   */
  const [percurso, definirPercurso] = useState<
    (Percurso & { atual: number; resultado: string }) | null
  >(null);
  useEffect(() => {
    if (!percurso || percurso.atual >= percurso.nos.length) return;
    const prazo = window.setTimeout(
      () => definirPercurso((p) => p && { ...p, atual: p.atual + 1 }),
      PASSO_DO_PERCURSO_MS,
    );
    return () => window.clearTimeout(prazo);
  }, [percurso]);
  /**
   * A última simulação: o painel reabre com ela, e "Ver o resultado" a mostra
   * sem simular de novo. Salvar a apaga, porque ela é da versão de antes.
   */
  const [ultimaSimulacao, definirUltimaSimulacao] = useState<Simulacao | null>(null);
  const instancia = useRef<ReactFlowInstance<Node<DadosDoBloco>, Edge<DadosDaLigacao>> | null>(
    null,
  );
  const idDoSimular = useId();
  /** "Testar como conversa": o mapa como está na tela, sem salvar (#1012). */
  const [testando, definirTestando] = useState(false);
  const pilulaDoPercurso = useRef<HTMLDivElement>(null);
  /*
   * O foco vai para a pílula quando o caminho abre, e volta ao "Simular" quando
   * ela fecha: sem isso, os botões dela somem com o foco dentro.
   */
  const percursoAberto = percurso !== null;
  useEffect(() => {
    if (percursoAberto) pilulaDoPercurso.current?.focus();
  }, [percursoAberto]);
  const fecharPercurso = (): void => {
    definirPercurso(null);
    window.setTimeout(() => document.getElementById(idDoSimular)?.focus(), 0);
  };
  const verNoMapa = (simulacao: Simulacao): void => {
    // A espera e a escolha do menu com ramos são o menu, no mapa.
    const passos = simulacao.dados.passos
      .map((p) => ({ ...p, no: p.no === null ? null : idNaTela(p.no) }))
      .filter((p, i, todos) => i === 0 || p.no !== todos[i - 1]!.no);
    const caminho = percursoDaSimulacao(
      passos,
      config.grafo,
      simulacao.dados.espera_em === null ? null : idNaTela(simulacao.dados.espera_em),
    );
    definirUltimaSimulacao(simulacao);
    definirSimulando(false);
    definirSelecionado(null);
    definirLigacaoEscolhida(null);
    definirPercurso({
      ...caminho,
      resultado: simulacao.dados.resultado,
      atual: movimentoReduzido() ? caminho.nos.length : 0,
    });
  };
  const [dicaDoRascunho, definirDicaDoRascunho] = useState(recemCriado);
  /** A saída de onde sai o próximo passo, enquanto a pessoa escolhe qual. */
  const [proximoPasso, definirProximoPasso] = useState<{
    de: string;
    saida: string;
    ordem: number;
  } | null>(null);
  /** O que tirar desfaz: a cópia de antes, por alguns segundos. */
  const [desfazer, definirDesfazer] = useState<{
    texto: string;
    config: Configuracao;
    sujo: boolean;
    casosNovos: Record<string, string[]>;
  } | null>(null);
  useEffect(() => {
    if (!desfazer) return;
    const prazo = window.setTimeout(() => definirDesfazer(null), TEMPO_PARA_DESFAZER_MS);
    return () => window.clearTimeout(prazo);
  }, [desfazer]);
  /** As medidas de cada bloco: reaplicadas, o mapa não remede tudo a cada mudança. */
  const medidas = useRef(new Map<string, { width: number; height: number }>());
  /** A cópia que foi mandada ao salvar: os erros do serviço falam dela. */
  const enviada = useRef<Configuracao>(config);

  // Fechar a aba com alteração não salva pergunta antes.
  useEffect(() => {
    if (!sujo) return;
    const aoSairDaPagina = (e: BeforeUnloadEvent): void => e.preventDefault();
    window.addEventListener('beforeunload', aoSairDaPagina);
    return () => window.removeEventListener('beforeunload', aoSairDaPagina);
  }, [sujo]);

  // Trocar de tela pelo menu ou pelo voltar do navegador também pergunta.
  useEffect(() => {
    if (!sujo) return;
    return registrarGuardaDeSaida((seguir) => {
      salvar.reset();
      definirSaidaPendente(() => seguir);
      return true;
    });
    // O `reset` só limpa o erro da última tentativa: não precisa estar nas dependências.
  }, [sujo]);

  // As consultas que o mapa usa: quantos valores cada uma pede (só quem monta com SQL as lê).
  const consultas = useOperacoesDeDados(
    'consulta',
    papel !== null && MONTA_AUTOMACAO_COM_SQL.has(papel),
  );
  const valoresDasConsultas = useMemo(
    () => Object.fromEntries((consultas.data?.itens ?? []).map((o) => [o.id, o.parametros.length])),
    [consultas.data],
  );
  const faltas = useMemo(
    () => pendencias(config, blocos, valoresDasConsultas),
    [config, blocos, valoresDasConsultas],
  );
  // Os casos novos de um bloco que saiu vão junto: o id pode voltar num bloco novo.
  const casosDosBlocos = (proxima: Configuracao): Record<string, string[]> =>
    Object.fromEntries(
      Object.entries(casosNovos).filter(([id]) => proxima.grafo.nos.some((n) => n.id === id)),
    );
  const problemas = useMemo(() => {
    const todos: Record<string, string[]> = { ...faltas };
    for (const [id, lista] of Object.entries(errosDoServico))
      todos[id] = [...(todos[id] ?? []), ...lista];
    return todos;
  }, [faltas, errosDoServico]);
  const quantasFaltas = Object.values(faltas).reduce((s, l) => s + l.length, 0);
  // A pílula conta o que o mapa marca: o que falta e o que o servidor recusou.
  const quantosProblemas = Object.values(problemas).reduce((s, l) => s + l.length, 0);

  /*
   * Quem pode pôr no ar a versão em teste: quem ativa, e a Cliqfy só as com SQL
   * (o mesmo `podeLigar` da lista).
   */
  const podeAtivar = (a: Pick<typeof automacao, 'situacao' | 'pendente' | 'usa_sql'>): boolean =>
    a.situacao === 'ativa' &&
    a.pendente !== null &&
    papel !== null &&
    ATIVA_AUTOMACAO.has(papel) &&
    (papel !== 'operacao_cliqfy' || a.usa_sql);

  const salvar = useMutation({
    /** `versao`: a do "Salvar por cima", que salva sobre a versão relida agora. */
    mutationFn: (versao?: number) => {
      // O que foi ao serviço: é nele que a posição de um erro aponta o bloco.
      enviada.current = expandirMenus(config);
      return api!.editarAutomacao({
        caminho: { id: automacao.id },
        corpo: {
          // O gatilho `interno` não chega aqui: quem o tem não abre a edição.
          gatilho: config.gatilho as EditarAutomacao['gatilho'],
          condicao: config.condicao,
          grafo: enviada.current.grafo,
          versao: versao ?? versaoLida,
        },
      });
    },
    onSuccess: (resposta) => {
      cache.setQueryData(chaves.automacao(automacao.id), resposta);
      void cache.invalidateQueries({ queryKey: chaves.automacoes() });
      definirSujo(false);
      definirUltimaSimulacao(null);
      const nova = resposta.pendente?.versao ?? resposta.versao;
      definirVersaoLida(nova);
      definirSalvo(
        resposta.situacao === 'ativa'
          ? `Salvo como versão ${nova}, em teste. A versão ${resposta.versao} continua rodando até alguém ativar a nova.`
          : `Salvo como versão ${nova}.`,
      );
      /*
       * A automação no ar segue rodando a versão de antes da mudança (26/10: a URA
       * corrigida falhou de novo porque a correção ficou em teste, sem ninguém
       * notar). Quem pode ativar responde na hora: pôr no ar ou deixar em teste.
       */
      if (podeAtivar(resposta)) {
        ativar.reset();
        definirConfirmandoAtivacao(true);
      }
    },
    onError: (erro) => {
      if (erro instanceof ErroDaApi && erro.problema?.errors?.length) {
        definirErrosDoServico(errosPorBloco(erro.problema.errors, enviada.current));
      }
    },
  });

  /*
   * O primeiro salvar pede o nome (26/10): sem ele, a automação ficava
   * "Automação sem nome" na lista. O nome é gravado antes do mapa, e o que vem
   * depois do salvar (sair do editor) continua depois dos dois.
   */
  /*
   * A versão sobe por fora do editor: ligar, pausar ou pôr em teste (cada
   * mudança de situação vira versão nova no serviço), outra aba, outra pessoa.
   * Sem nada por salvar aqui, a cópia passa a ser a relida, com o mapa dela:
   * sem isso, o próximo salvar seria recusado por uma versão que só a situação
   * mudou, e a saída era recarregar a página (27/10).
   */
  const versaoDaRelida = versaoEditada(automacao);
  useEffect(() => {
    if (sujo || salvar.isPending || versaoDaRelida === versaoLida) return;
    const relida = configuracaoDe(automacao);
    if (relida) definirConfig(comPosicoes(relida, blocos));
    definirVersaoLida(versaoDaRelida);
  }, [automacao, blocos, sujo, salvar.isPending, versaoDaRelida, versaoLida]);

  /*
   * O conflito de verdade: alguém salvou o mapa enquanto havia mudança aqui.
   * Duas saídas, as duas sem recarregar a página: trazer a versão de lá
   * (perde o que mudou aqui) ou salvar por cima (o de lá fica no histórico).
   */
  const conflito =
    salvar.error instanceof ErroDaApi && salvar.error.codigo === 'CONFLITO_DE_VERSAO';
  const relerAutomacao = async (): Promise<AutomacaoDaApi> => {
    const atual = await api!.automacao({ caminho: { id: automacao.id } });
    cache.setQueryData(chaves.automacao(automacao.id), atual);
    return atual;
  };
  const carregarAtual = useMutation({
    mutationFn: relerAutomacao,
    onSuccess: (atual) => {
      const relida = configuracaoDe(atual);
      if (relida) definirConfig(comPosicoes(relida, blocos));
      definirVersaoLida(versaoEditada(atual));
      definirSujo(false);
      definirErrosDoServico({});
      salvar.reset();
      definirSalvo(`Carregada a versão ${versaoEditada(atual)}, a mais recente.`);
    },
  });
  const salvarPorCima = useMutation({
    mutationFn: relerAutomacao,
    onSuccess: (atual) => {
      const versao = versaoEditada(atual);
      definirVersaoLida(versao);
      salvar.mutate(versao);
    },
  });

  const [nomeando, definirNomeando] = useState<{ readonly depois?: () => void } | null>(null);
  const nomear = useMutation({
    mutationFn: ({ nome, descricao }: { nome: string; descricao: string }) =>
      api!.identificarAutomacao({ caminho: { id: automacao.id }, corpo: { nome, descricao } }),
    onSuccess: (identificada) => {
      cache.setQueryData(chaves.automacao(automacao.id), identificada);
      void cache.invalidateQueries({ queryKey: chaves.automacoes() });
    },
  });
  /*
   * O que vinha depois do salvar (sair do editor) espera a pergunta de pôr no
   * ar: saindo junto, ela sumiria, e a versão de antes seguiria rodando.
   */
  const depoisDaPergunta = useRef<(() => void) | null>(null);
  const depoisDoSalvar = (resposta: typeof automacao, depois: () => void): void => {
    if (podeAtivar(resposta)) depoisDaPergunta.current = depois;
    else depois();
  };
  const fecharPergunta = (): void => {
    definirConfirmandoAtivacao(false);
    const depois = depoisDaPergunta.current;
    depoisDaPergunta.current = null;
    depois?.();
  };

  const pedirSalvar = (depois?: () => void): void => {
    if (!automacao.nome?.trim()) {
      nomear.reset();
      definirNomeando(depois ? { depois } : {});
      return;
    }
    salvar.mutate(undefined, depois ? { onSuccess: (r) => depoisDoSalvar(r, depois) } : undefined);
  };

  /*
   * Conferir é a mesma checagem do salvar, sem gravar nada: os problemas voltam
   * por bloco, como quando o salvar é recusado.
   */
  const conferir = useMutation({
    mutationFn: () => {
      // O que foi ao serviço: é nele que a posição de um erro aponta o bloco.
      enviada.current = expandirMenus(config);
      return api!.validarAutomacao({
        corpo: {
          gatilho: config.gatilho as EditarAutomacao['gatilho'],
          condicao: config.condicao,
          grafo: enviada.current.grafo,
        },
      });
    },
    onMutate: () => {
      definirConferido(false);
      definirErrosDoServico({});
    },
    onSuccess: () => definirConferido(true),
    onError: (erro) => {
      if (erro instanceof ErroDaApi && erro.problema?.errors?.length) {
        definirErrosDoServico(errosPorBloco(erro.problema.errors, enviada.current));
      }
    },
  });

  /*
   * A versão em teste de uma automação ativa: descartar joga fora a mudança, e
   * a versão que está no ar continua. O mapa volta para ela.
   */
  const temVersaoEmTeste = automacao.situacao === 'ativa' && automacao.pendente !== null;
  const descartar = useMutation({
    mutationFn: () => api!.descartarPendenteDaAutomacao({ caminho: { id: automacao.id } }),
    onSuccess: (resposta) => {
      cache.setQueryData(chaves.automacao(automacao.id), resposta);
      void cache.invalidateQueries({ queryKey: chaves.automacoes() });
      const doAr = configuracaoDe(resposta);
      if (doAr) definirConfig(comPosicoes(doAr, blocos));
      definirVersaoLida(versaoEditada(resposta));
      definirSujo(false);
      definirErrosDoServico({});
      definirCasosNovos({});
      definirConferido(false);
      definirConfirmandoDescarte(false);
      // O caminho simulado era da versão descartada.
      definirPercurso(null);
      definirUltimaSimulacao(null);
      definirSalvo(
        `A versão em teste foi descartada. Vale a versão ${resposta.versao}, que está no ar.`,
      );
    },
  });

  /*
   * Ativar a versão em teste: o serviço promove a pendente, e a que estava no ar
   * sai. Pedido logo depois de salvar, e pelo botão do cabeçalho.
   */
  const podeAtivarAVersaoEmTeste = temVersaoEmTeste && podeAtivar(automacao);
  const ativar = useMutation({
    mutationFn: () => api!.ativarAutomacao({ caminho: { id: automacao.id } }),
    onSuccess: (resposta) => {
      cache.setQueryData(chaves.automacao(automacao.id), resposta);
      void cache.invalidateQueries({ queryKey: chaves.automacoes() });
      definirVersaoLida(versaoEditada(resposta));
      definirConferido(false);
      definirSalvo(`A versão ${resposta.versao} está no ar. Ela roda a partir de agora.`);
      fecharPergunta();
    },
  });

  // Enquanto salva, nada muda: o que mudasse ficaria marcado como salvo sem estar.
  const mudar = (proxima: Configuracao): void => {
    if (salvar.isPending) return;
    definirConfig(proxima);
    // O caminho simulado é da versão salva: mexeu, ele não vale mais.
    definirPercurso(null);
    definirCasosNovos(casosDosBlocos(proxima));
    definirSujo(true);
    definirSalvo(null);
    definirConferido(false);
    // O que foi conferido era a cópia de antes: o aviso dela sai junto.
    if (conferir.isError) conferir.reset();
    definirErrosDoServico({});
  };

  /*
   * Os gestos que o bloco e a ligação chamam ("+", "×") passam por aqui: o
   * desenho não muda quando só a função muda.
   */
  const gestos = useRef({
    proximo: (_de: string, _saida: string, _ordem: number): void => {},
    tirar: (_de: string, _saida: string): void => {},
  });

  const { nodes, edges } = useMemo(() => {
    // Pela posição no caminho, e não pela primeira vez que o bloco aparece nele.
    const passoDo = (id: string): DadosDoBloco['passo'] => {
      if (!percurso) return undefined;
      if (percurso.atual < percurso.nos.length && percurso.nos[percurso.atual] === id) {
        return 'agora';
      }
      if (!percurso.nos.slice(0, percurso.atual).includes(id)) return 'fora';
      return id === percurso.falhou ? 'falhou' : 'feito';
    };
    /** A ligação no caminho: a última percorrida corre; as que ficaram de fora apagam. */
    const caminhoDa = (id: string): DadosDaLigacao['caminho'] => {
      if (!percurso) return undefined;
      const posicoes = percurso.ligacoes.flatMap((l, k) => (l === id ? [k] : []));
      const andando = percurso.atual < percurso.nos.length;
      if (andando && posicoes.includes(percurso.atual - 1)) return 'correndo';
      return posicoes.some((k) => k < percurso.atual) ? 'feita' : 'fora';
    };
    const desenho = desenharMapa(config, blocos, casosNovos);
    const nodes: Node<DadosDoBloco>[] = desenho.blocos.map(({ id, posicao, ...b }) => {
      const medida = medidas.current.get(id);
      return {
        id,
        type: 'bloco',
        position: posicao,
        selected: id === selecionado,
        ...(id === ID_DO_COMECO ? { deletable: false, draggable: false } : {}),
        ...(medida ? { measured: medida } : {}),
        ariaLabel: `${b.titulo}: ${b.resumo}`,
        data: {
          ...b,
          faltam: problemas[id]?.length ?? 0,
          novo: id === recemPosto,
          passo: passoDo(id),
          aoProximo: (saida: string, ordem: number) => gestos.current.proximo(id, saida, ordem),
        },
      };
    });
    const edges = desenho.ligacoes.map((l) =>
      ligacaoParaOMapa(l, {
        selected: ligacaoEscolhida === l.id,
        aoTirar: () => gestos.current.tirar(l.de, l.saida),
        ...(percurso ? { caminho: caminhoDa(l.id) } : {}),
      }),
    );
    return { nodes, edges };
  }, [config, blocos, problemas, selecionado, casosNovos, ligacaoEscolhida, recemPosto, percurso]);

  // ── Tirar, com desfazer; duplicar; o próximo passo de uma saída ─────────

  const tirarBlocos = (ids: readonly string[]): void => {
    const fora = ids.filter((id) => id !== ID_DO_COMECO);
    if (fora.length === 0 || salvar.isPending) return;
    const um = fora.length === 1 ? config.grafo.nos.find((n) => n.id === fora[0]) : undefined;
    definirDesfazer({
      texto: um
        ? `“${descreverNo(um, blocos).titulo}” saiu do mapa.`
        : `${fora.length} blocos saíram do mapa.`,
      config,
      sujo,
      casosNovos,
    });
    if (selecionado && fora.includes(selecionado)) definirSelecionado(null);
    mudar(removerBlocos(config, fora));
  };

  const desfazerRemocao = (): void => {
    if (!desfazer || salvar.isPending) return;
    definirConfig(desfazer.config);
    definirSujo(desfazer.sujo);
    definirCasosNovos(desfazer.casosNovos);
    definirErrosDoServico({});
    definirConferido(false);
    definirDesfazer(null);
  };

  const duplicar = (id: string): void => {
    const r = duplicarBloco(config, id);
    if (!r) return;
    mudar(r.configuracao);
    definirSelecionado(r.id);
    definirRecemPosto(r.id);
  };

  const porDepois = (item: ItemDaPaleta): void => {
    if (!proximoPasso) return;
    const de = nodes.find((n) => n.id === proximoPasso.de);
    if (!de) return;
    const { configuracao, id } = adicionarDepois(
      config,
      { id: de.id, posicao: de.position },
      proximoPasso.saida,
      proximoPasso.ordem,
      item,
      blocos,
    );
    definirProximoPasso(null);
    mudar(configuracao);
    definirSelecionado(id);
    definirRecemPosto(id);
  };

  gestos.current = {
    proximo: (de, saida, ordem) => definirProximoPasso({ de, saida, ordem }),
    tirar: (de, saida) => {
      mudar(desligar(config, de, saida));
      definirLigacaoEscolhida(null);
    },
  };

  const aoMudarBlocos = (mudancas: NodeChange<Node<DadosDoBloco>>[]): void => {
    let proxima = config;
    let mexeu = false;
    const removidos: string[] = [];
    for (const m of mudancas) {
      if (m.type === 'position' && m.position && m.id !== ID_DO_COMECO) {
        proxima = mover(proxima, m.id, Math.round(m.position.x), Math.round(m.position.y));
        mexeu = true;
      } else if (m.type === 'remove') {
        removidos.push(m.id);
      } else if (m.type === 'select' && m.selected) {
        definirSelecionado(m.id);
        definirLigacaoEscolhida(null);
      } else if (m.type === 'dimensions' && m.dimensions) {
        medidas.current.set(m.id, m.dimensions);
      }
    }
    // Tirar pelo teclado também deixa desfazer.
    if (removidos.length > 0) tirarBlocos(removidos);
    else if (mexeu) mudar(proxima);
  };

  const aoMudarLigacoes = (mudancas: EdgeChange[]): void => {
    let proxima = config;
    let mexeu = false;
    for (const m of mudancas) {
      if (m.type === 'select') {
        definirLigacaoEscolhida(m.selected ? m.id : null);
        continue;
      }
      if (m.type !== 'remove') continue;
      // O id é `bloco|saída`; o nome de um caso pode ter "|", o id do bloco não.
      const corte = m.id.indexOf('|');
      proxima = desligar(proxima, m.id.slice(0, corte), m.id.slice(corte + 1));
      definirLigacaoEscolhida(null);
      mexeu = true;
    }
    if (mexeu) mudar(proxima);
  };

  const aoLigar = (c: Connection): void => {
    if (!c.source || !c.target) return;
    mudar(ligar(config, c.source, c.sourceHandle ?? PROXIMO, c.target));
  };

  // Trocar o início é trocar o gatilho: o bloco "Começa quando" fica escolhido,
  // para a pessoa ver e ajustar o novo (a agenda, o "Só se").
  const porInicio = (inicio: InicioDaPaleta): void => {
    mudar(trocarInicio(config, inicio));
    definirSelecionado(ID_DO_COMECO);
  };

  const porBloco = (item: ItemDaPaleta): void => {
    // O bloco novo entra à direita do escolhido, ou à direita de tudo.
    const base = config.grafo.nos.find((n) => n.id === selecionado)?.posicao;
    const maisDireita = Math.max(0, ...config.grafo.nos.map((n) => n.posicao?.x ?? 0));
    const posicao = base ? { x: base.x + 300, y: base.y + 40 } : { x: maisDireita + 300, y: 40 };
    const { configuracao, id } = adicionarBloco(config, item, posicao, blocos);
    mudar(configuracao);
    definirSelecionado(id);
    definirRecemPosto(id);
  };

  const voltar = (): void => {
    if (!sujo) return aoSair();
    salvar.reset();
    definirSaidaPendente(() => aoSair);
  };

  /** Como o caminho simulado terminou, e onde parou quando falhou. */
  const fimDoPercurso = (p: NonNullable<typeof percurso>) => {
    const titulo = (id: string | undefined): string =>
      nodes.find((n) => n.id === id)?.data.titulo ?? '';
    const rotulo = RESULTADO_DA_SIMULACAO[p.resultado]?.rotulo ?? p.resultado;
    const total = p.nos.length;
    // O serviço hoje não manda o passo que falhou: sem ele, o texto diz depois de qual bloco.
    const ondeParou =
      p.resultado !== 'falhou'
        ? ''
        : p.falhou
          ? `em “${titulo(p.falhou)}”`
          : p.dentroDe
            ? `dentro de “${titulo(p.dentroDe)}”`
            : total > 1
              ? `depois de “${titulo(p.nos[total - 1])}”`
              : '';
    return { titulo, rotulo, ondeParou };
  };
  /*
   * O que o leitor de tela ouve do caminho: que começou e como terminou, e não
   * um anúncio a cada bloco. A região mora fora da pílula e existe antes dela:
   * com menos movimento a pílula já nasce no fim, e um texto que entra junto
   * com a região não é anunciado.
   */
  const anuncioDoPercurso = ((): string => {
    if (!percurso) return '';
    if (percurso.atual < percurso.nos.length) {
      return percurso.atual > 0 ? 'Andando pelo caminho simulado.' : '';
    }
    const { rotulo, ondeParou } = fimDoPercurso(percurso);
    return `Simulação no mapa: ${rotulo}${ondeParou ? `, ${ondeParou}` : ''}.`;
  })();

  /** A pílula do caminho simulado, no alto do mapa. */
  const painelDoPercurso = (): ReactNode => {
    if (!percurso) return null;
    const total = percurso.nos.length;
    const terminou = percurso.atual >= total;
    const { titulo, rotulo, ondeParou } = fimDoPercurso(percurso);
    return (
      <Panel position="top-center">
        <div
          ref={pilulaDoPercurso}
          className="editor-do-mapa__percurso"
          role="group"
          aria-label="Simulação no mapa"
          tabIndex={-1}
        >
          <span className="editor-do-mapa__percurso-icone" aria-hidden="true">
            <Icone nome="automacao" />
          </span>
          <p className="editor-do-mapa__percurso-texto">
            {terminou ? (
              <>
                {'Simulação no mapa: '}
                <Selo tom={RESULTADO_DA_SIMULACAO[percurso.resultado]?.tom ?? 'neutro'}>
                  {rotulo}
                </Selo>
                {ondeParou && <span>{ondeParou}</span>}
              </>
            ) : (
              <>
                {`Simulando · passo ${percurso.atual + 1} de ${total}: `}
                <strong>{titulo(percurso.nos[percurso.atual])}</strong>
              </>
            )}
          </p>
          {ultimaSimulacao && (
            <Botao variante="fantasma" miudo onClick={() => definirSimulando(true)}>
              <Icone nome="documento" />
              Ver o resultado
            </Botao>
          )}
          {terminou && total > 1 && !movimentoReduzido() && (
            <Botao
              variante="fantasma"
              miudo
              onClick={() => {
                definirPercurso((p) => p && { ...p, atual: 0 });
                // O botão some enquanto o caminho anda: o foco fica na pílula.
                pilulaDoPercurso.current?.focus();
              }}
            >
              <Icone nome="repetir" />
              Ver de novo
            </Botao>
          )}
          <Botao variante="secundario" miudo onClick={fecharPercurso}>
            <Icone nome="fechar" />
            {terminou ? 'Fechar' : 'Parar'}
          </Botao>
        </div>
      </Panel>
    );
  };

  const paleta = buscarNaPaleta(itensDaPaleta(blocos), buscaNaPaleta);
  const inicios = iniciosDaPaleta(blocos).filter((i) =>
    semAcento(i.titulo).includes(semAcento(buscaNaPaleta)),
  );
  // O primeiro bloco com o que acertar: a pílula da barra leva até ele.
  const primeiroAAcertar = Object.keys(problemas)[0] ?? null;
  const irParaOBloco = (id: string): void => {
    definirLigacaoEscolhida(null);
    definirSelecionado(id);
    void instancia.current?.fitView({
      nodes: [{ id }],
      duration: movimentoReduzido() ? 0 : 300,
      maxZoom: 1.1,
      padding: 0.4,
    });
  };
  const GRUPOS: { grupo: ItemDaPaleta['grupo']; titulo: string }[] = [
    { grupo: 'acao', titulo: 'Ações' },
    { grupo: 'logica', titulo: 'Lógica' },
    { grupo: 'espera', titulo: 'Esperas' },
    { grupo: 'dados', titulo: 'Dados' },
  ];

  return (
    <div className="editor-do-mapa__edicao">
      <header className="editor-do-mapa__barra">
        <Botao variante="fantasma" icone rotulo="Voltar para Automações" onClick={voltar}>
          <Icone nome="voltar" />
        </Botao>
        <span className="editor-do-mapa__marca" aria-hidden="true">
          <Icone nome="automacao" />
        </span>
        <div className="editor-do-mapa__titulos">
          <span className="editor-do-mapa__trilha">Automações › Mapa da automação</span>
          <h1 className="editor-do-mapa__titulo">{cabecalho?.titulo ?? 'Automação'}</h1>
        </div>
        <span className="editor-do-mapa__versao" title={cabecalho?.apoio ?? `Versão ${versaoLida}`}>
          v{versaoLida}
        </span>
        {cabecalho?.selo}
        {quantosProblemas > 0 ? (
          <button
            type="button"
            className="editor-do-mapa__pendencias-pilula"
            data-tom="atencao"
            title="Ir para o primeiro bloco com o que acertar"
            onClick={() => primeiroAAcertar && irParaOBloco(primeiroAAcertar)}
          >
            <Icone nome="alerta" />
            {quantosProblemas === 1 ? '1 pendência' : `${quantosProblemas} pendências`}
            <span className="visualmente-oculto">: ir para o primeiro bloco</span>
          </button>
        ) : (
          <span className="editor-do-mapa__pendencias-pilula" data-tom="ok">
            <Icone nome="check" />
            Nada a acertar
          </span>
        )}
        <p className="editor-do-mapa__estado" role="status" aria-live="polite">
          {conferido
            ? sujo
              ? 'Conferido: nada a acertar. Salve para virar uma versão nova.'
              : 'Conferido: nada a acertar.'
            : sujo
              ? 'Alterações não salvas: viram uma versão nova ao salvar.'
              : (salvo ?? `Versão ${versaoLida}, sem alterações.`)}
        </p>
        <div className="editor-do-mapa__acoes">
          {temVersaoEmTeste && !sujo && (
            <Botao
              variante="fantasma"
              onClick={() => {
                descartar.reset();
                definirConfirmandoDescarte(true);
              }}
              desabilitado={salvar.isPending}
              motivo="Salvando…"
            >
              Descartar a versão em teste
            </Botao>
          )}
          {podeAtivarAVersaoEmTeste && !sujo && automacao.pendente && (
            <Botao
              variante="primario"
              onClick={() => {
                ativar.reset();
                definirConfirmandoAtivacao(true);
              }}
              desabilitado={salvar.isPending}
              motivo="Salvando…"
            >
              {`Ativar a v${automacao.pendente.versao}`}
            </Botao>
          )}
          <Botao
            variante="secundario"
            onClick={() => conferir.mutate()}
            desabilitado={conferir.isPending || salvar.isPending || api === null}
            motivo={
              conferir.isPending
                ? 'Conferindo…'
                : salvar.isPending
                  ? 'Salvando…'
                  : 'Sem sessão: entre de novo.'
            }
          >
            {conferir.isPending ? 'Conferindo…' : 'Conferir'}
          </Botao>
          <Botao variante="secundario" onClick={() => definirTestando(true)}>
            Testar como conversa
          </Botao>
          <Botao
            id={idDoSimular}
            variante="secundario"
            onClick={() => definirSimulando(true)}
            desabilitado={sujo}
            motivo="Salve antes: a simulação roda a versão salva."
          >
            Simular
          </Botao>
          {sujo && (
            <Botao
              variante="secundario"
              onClick={() => {
                definirConfig(comPosicoes(inicial, blocos));
                definirSujo(false);
                definirErrosDoServico({});
                definirCasosNovos({});
                definirPercurso(null);
              }}
              desabilitado={salvar.isPending}
              motivo="Salvando…"
            >
              Descartar
            </Botao>
          )}
          {cabecalho?.historico}
          <Botao
            variante="primario"
            onClick={() => pedirSalvar()}
            desabilitado={!sujo || quantasFaltas > 0 || salvar.isPending || api === null}
            motivo={
              salvar.isPending
                ? 'Salvando…'
                : !sujo
                  ? 'Nada mudou desde a última versão.'
                  : `Falta acertar ${quantasFaltas === 1 ? '1 coisa' : `${quantasFaltas} coisas`} antes de salvar.`
            }
          >
            {salvar.isPending ? 'Salvando…' : sujo ? 'Salvar rascunho' : 'Tudo salvo'}
          </Botao>
        </div>
      </header>

      {conferir.isError && (
        <Faixa tom="bloqueado">
          {erroDosBlocos(conferir.error)
            ? 'O servidor achou o que acertar: os blocos estão marcados no mapa.'
            : recadoDeErro(conferir.error, {
                generica: 'Não foi possível conferir agora. Tente de novo.',
                porCodigo: {
                  ENGINE_INDISPONIVEL:
                    'O serviço das automações não respondeu. Tente de novo daqui a pouco.',
                },
              })}
        </Faixa>
      )}
      {conflito && (
        <div className="editor-do-mapa__conflito" role="alert">
          <p>
            <strong>Alguém salvou esta automação depois que você abriu.</strong> As suas mudanças
            ainda não foram salvas. Traga a versão de lá (as mudanças daqui se perdem) ou salve as
            suas por cima: a versão de lá continua no histórico.
          </p>
          <div className="editor-do-mapa__conflito-acoes">
            <Botao
              miudo
              onClick={() => carregarAtual.mutate()}
              desabilitado={carregarAtual.isPending || salvarPorCima.isPending}
              motivo="Aguarde: carregando."
            >
              {carregarAtual.isPending ? 'Carregando…' : 'Carregar a versão atual'}
            </Botao>
            <Botao
              miudo
              variante="primario"
              onClick={() => salvarPorCima.mutate()}
              desabilitado={carregarAtual.isPending || salvarPorCima.isPending}
              motivo="Aguarde: salvando."
            >
              {salvarPorCima.isPending ? 'Salvando…' : 'Salvar por cima'}
            </Botao>
          </div>
          {(carregarAtual.isError || salvarPorCima.isError) && (
            <p>
              {recadoDeErro(carregarAtual.error ?? salvarPorCima.error, {
                generica: 'Não deu para ler a versão atual agora. Tente de novo.',
              })}
            </p>
          )}
        </div>
      )}
      {salvar.isError && !conflito && (
        <Faixa tom="bloqueado">
          {recadoDeErro(salvar.error, {
            generica: 'Não foi possível salvar agora. Nada mudou; tente de novo.',
            porCodigo: {
              ENGINE_INDISPONIVEL:
                'O serviço das automações não respondeu. Nada mudou; tente de novo daqui a pouco.',
            },
          })}
        </Faixa>
      )}

      <div className="editor-do-mapa__colunas">
        <nav className="editor-do-mapa__paleta" aria-label="Blocos para o mapa">
          <Campo
            type="search"
            icone="buscar"
            tamanho="miudo"
            rotuloOculto="Buscar bloco"
            placeholder="Buscar bloco"
            value={buscaNaPaleta}
            onChange={(e) => definirBuscaNaPaleta(e.target.value)}
          />
          {/* Vazia, a região fica fora da vista, para não abrir um espaço na paleta. */}
          <p
            className={
              paleta.length === 0 && inicios.length === 0 && buscaNaPaleta.trim() !== ''
                ? 'editor-do-mapa__painel-ajuda'
                : 'visualmente-oculto'
            }
            role="status"
          >
            {paleta.length === 0 && inicios.length === 0 && buscaNaPaleta.trim() !== ''
              ? `Nenhum bloco com “${buscaNaPaleta.trim()}”.`
              : ''}
          </p>
          {inicios.length > 0 && (
            <section className="editor-do-mapa__paleta-grupo">
              <h2 className="editor-do-mapa__paleta-titulo">
                Começa quando
                <span className="editor-do-mapa__paleta-nota">troca o início</span>
              </h2>
              <ul>
                {inicios.map((i) => {
                  const atual = ehOInicioAtual(config.gatilho, i);
                  return (
                    <li key={i.chave}>
                      <button
                        type="button"
                        className="editor-do-mapa__paleta-item"
                        data-grupo="inicio"
                        aria-pressed={atual}
                        title={i.descricao}
                        onClick={() => porInicio(i)}
                        aria-label={`Começar por: ${i.titulo}${atual ? ' (o início de agora)' : ''}`}
                      >
                        <span className="editor-do-mapa__paleta-icone" aria-hidden="true">
                          <Icone nome={i.gatilho.tipo === 'agenda' ? 'relogio' : 'play'} />
                        </span>
                        {i.titulo}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </section>
          )}
          {GRUPOS.map(({ grupo, titulo }) => {
            const itens = paleta.filter((i) => i.grupo === grupo);
            if (itens.length === 0) return null;
            return (
              <section key={grupo} className="editor-do-mapa__paleta-grupo">
                <h2 className="editor-do-mapa__paleta-titulo">{titulo}</h2>
                <ul>
                  {itens.map((i) => (
                    <li key={i.chave}>
                      <button
                        type="button"
                        className="editor-do-mapa__paleta-item"
                        data-grupo={i.grupo}
                        onClick={() => porBloco(i)}
                        aria-label={`Pôr no mapa: ${i.titulo}`}
                      >
                        <span className="editor-do-mapa__paleta-icone" aria-hidden="true">
                          <Icone nome={iconeDoTipo(i.tipo)} />
                        </span>
                        {i.titulo}
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            );
          })}
        </nav>

        <section className="editor-do-mapa__plano" aria-label="Mapa da automação">
          <ReactFlow
            nodes={nodes}
            edges={edges}
            nodeTypes={TIPOS_DE_BLOCO}
            edgeTypes={TIPOS_DE_LIGACAO}
            onNodesChange={aoMudarBlocos}
            onEdgesChange={aoMudarLigacoes}
            onConnect={aoLigar}
            onInit={(i) => {
              instancia.current = i;
            }}
            onPaneClick={() => {
              definirSelecionado(null);
              definirLigacaoEscolhida(null);
            }}
            deleteKeyCode={['Delete', 'Backspace']}
            nodesDraggable={!salvar.isPending}
            nodesConnectable={!salvar.isPending}
            fitView
            minZoom={0.35}
            maxZoom={1.6}
            snapToGrid
            snapGrid={[8, 8]}
            connectionLineStyle={{
              stroke: 'var(--roxo)',
              strokeWidth: 2,
              strokeDasharray: '6 5',
            }}
          >
            <Background gap={16} />
            <FerramentasDoMapa aoOrganizar={() => mudar(organizarTudo(config, blocos))} />
            {percurso && painelDoPercurso()}
            <LegendaDoMapa />
            {dicaDoRascunho && (
              <Panel position="top-left">
                <div className="editor-do-mapa__dica" role="note">
                  <span className="editor-do-mapa__dica-icone" aria-hidden="true">
                    <Icone nome="lapis" />
                  </span>
                  <p>
                    <strong>Rascunho criado.</strong> Ele ainda não roda. Escolha como ele começa em
                    “Começa quando”, na paleta; troque o bloco “Esperar um tempo” pelo primeiro
                    passo; e salve. No primeiro salvar, você dá o nome a ela.
                  </p>
                  <Botao
                    variante="fantasma"
                    miudo
                    icone
                    rotulo="Fechar o recado"
                    onClick={() => definirDicaDoRascunho(false)}
                  >
                    <Icone nome="fechar" />
                  </Botao>
                </div>
              </Panel>
            )}
          </ReactFlow>
        </section>

        <PainelDoBloco
          config={config}
          blocos={blocos}
          papel={papel}
          selecionado={selecionado}
          problemas={problemas}
          casosNovos={selecionado ? (casosNovos[selecionado] ?? []) : []}
          aoMudar={mudar}
          aoLigar={(de, saida, para) =>
            mudar(para ? ligar(config, de, saida, para) : desligar(config, de, saida))
          }
          aoNovoCaso={(id, caso) =>
            definirCasosNovos((antes) => ({ ...antes, [id]: [...(antes[id] ?? []), caso] }))
          }
          aoTirarCasos={(id, casos) =>
            definirCasosNovos((antes) => ({
              ...antes,
              [id]: (antes[id] ?? []).filter((c) => !casos.includes(c)),
            }))
          }
          aoEscolher={definirSelecionado}
          aoDuplicar={duplicar}
          aoRemover={(id) => tirarBlocos([id])}
        />
      </div>

      <p className="visualmente-oculto" role="status" aria-live="polite">
        {anuncioDoPercurso}
      </p>

      {/* A região viva existe antes do recado, para o leitor de tela anunciar. */}
      <div className="editor-do-mapa__recados" role="status" aria-live="polite">
        {desfazer && (
          <p className="editor-do-mapa__recado">
            {desfazer.texto}
            <button type="button" className="editor-do-mapa__desfazer" onClick={desfazerRemocao}>
              <Icone nome="repetir" />
              Desfazer
            </button>
          </p>
        )}
      </div>

      {proximoPasso && (
        <ProximoPasso
          saida={
            nodes
              .find((n) => n.id === proximoPasso.de)
              ?.data.saidas.find((x) => x.id === proximoPasso.saida)?.rotulo ?? ''
          }
          bloco={nodes.find((n) => n.id === proximoPasso.de)?.data.titulo ?? ''}
          itens={itensDaPaleta(blocos)}
          aoEscolher={porDepois}
          aoFechar={() => definirProximoPasso(null)}
        />
      )}

      {testando && (
        <ConversaDeTeste config={config} blocos={blocos} aoFechar={() => definirTestando(false)} />
      )}

      {simulando && (
        <Simular
          automacao={automacao}
          blocos={blocos}
          versaoDoMapa={versaoLida}
          anterior={ultimaSimulacao}
          aoFechar={() => definirSimulando(false)}
          aoSimular={definirUltimaSimulacao}
          aoVerNoMapa={verNoMapa}
        />
      )}

      {confirmandoDescarte && automacao.pendente && (
        <Dialogo
          titulo="Descartar a versão em teste?"
          largura="estreita"
          papel="alertdialog"
          descricaoId={porQueDescartar}
          consequencia
          aoFechar={() => {
            if (!descartar.isPending) definirConfirmandoDescarte(false);
          }}
          botoes={
            <>
              <Botao
                variante="secundario"
                onClick={() => definirConfirmandoDescarte(false)}
                desabilitado={descartar.isPending}
                motivo="Descartando…"
              >
                Manter
              </Botao>
              <Botao
                variante="perigo"
                onClick={() => descartar.mutate()}
                desabilitado={descartar.isPending || api === null}
                motivo={descartar.isPending ? 'Descartando…' : 'Sem sessão: entre de novo.'}
              >
                {descartar.isPending ? 'Descartando…' : 'Descartar'}
              </Botao>
            </>
          }
        >
          <p id={porQueDescartar}>
            A versão {automacao.pendente.versao}, em teste, se perde. A versão {automacao.versao}{' '}
            continua no ar, sem mudança.
          </p>
          {descartar.isError && (
            <Faixa tom="bloqueado">
              {recadoDeErro(descartar.error, {
                generica: 'Não foi possível descartar agora. Nada mudou; tente de novo.',
                porCodigo: {
                  ENGINE_INDISPONIVEL:
                    'O serviço das automações não respondeu. Nada mudou; tente de novo daqui a pouco.',
                },
              })}
            </Faixa>
          )}
        </Dialogo>
      )}

      {confirmandoAtivacao && automacao.pendente && (
        <Dialogo
          titulo={`Pôr a versão ${automacao.pendente.versao} no ar?`}
          largura="estreita"
          papel="alertdialog"
          descricaoId={oQueAtivarFaz}
          aoFechar={() => {
            if (!ativar.isPending) fecharPergunta();
          }}
          botoes={
            <>
              <Botao
                variante="secundario"
                onClick={fecharPergunta}
                desabilitado={ativar.isPending}
                motivo="Ativando…"
              >
                Deixar em teste
              </Botao>
              <Botao
                variante="primario"
                onClick={() => ativar.mutate()}
                desabilitado={ativar.isPending || api === null}
                motivo={ativar.isPending ? 'Ativando…' : 'Sem sessão: entre de novo.'}
              >
                {ativar.isPending ? 'Ativando…' : 'Ativar'}
              </Botao>
            </>
          }
        >
          <p id={oQueAtivarFaz}>
            Até ativar, quem roda é a versão {automacao.versao}, sem as mudanças. Ativando, a versão{' '}
            {automacao.pendente.versao} passa a rodar no lugar dela a partir de agora; o que já
            estava em andamento termina na versão de antes.
          </p>
          {ativar.isError && (
            <Faixa tom="bloqueado">
              {recadoDeErro(ativar.error, {
                generica: 'Não foi possível ativar agora. Nada mudou; tente de novo.',
                porCodigo: {
                  ENGINE_INDISPONIVEL:
                    'O serviço das automações não respondeu. Nada mudou; tente de novo daqui a pouco.',
                },
              })}
            </Faixa>
          )}
        </Dialogo>
      )}

      {nomeando && (
        <NomearAutomacao
          salvando={nomear.isPending || salvar.isPending}
          erro={
            nomear.isError
              ? recadoDeErro(nomear.error, {
                  generica: 'Não foi possível salvar o nome agora. Nada mudou; tente de novo.',
                })
              : salvar.isError
                ? 'O nome foi salvo, mas o mapa não: confira os blocos marcados e salve de novo.'
                : null
          }
          aoConfirmar={(nome, descricao) =>
            nomear.mutate(
              { nome, descricao },
              {
                onSuccess: () =>
                  salvar.mutate(undefined, {
                    onSuccess: (r) => {
                      definirNomeando(null);
                      if (nomeando.depois) depoisDoSalvar(r, nomeando.depois);
                    },
                  }),
              },
            )
          }
          aoFechar={() => definirNomeando(null)}
        />
      )}

      {saidaPendente && (
        <Dialogo
          titulo="Salvar o rascunho?"
          largura="estreita"
          consequencia
          aoFechar={() => definirSaidaPendente(null)}
          botoes={
            <>
              <Botao
                variante="perigo"
                onClick={saidaPendente}
                desabilitado={salvar.isPending}
                motivo="Salvando…"
              >
                Descartar
              </Botao>
              <Botao
                variante="primario"
                onClick={() => pedirSalvar(() => saidaPendente())}
                desabilitado={quantasFaltas > 0 || salvar.isPending || api === null}
                motivo={
                  salvar.isPending
                    ? 'Salvando…'
                    : api === null
                      ? 'Sem sessão: entre de novo.'
                      : `Falta acertar ${quantasFaltas === 1 ? '1 coisa' : `${quantasFaltas} coisas`} antes de salvar.`
                }
              >
                {salvar.isPending ? 'Salvando…' : 'Salvar'}
              </Botao>
            </>
          }
        >
          <p>
            Deseja salvar o rascunho antes de sair? Sua edição não ficará salva caso queira
            descartar.
          </p>
          {quantasFaltas > 0 && (
            <p>
              {quantasFaltas === 1
                ? 'Falta acertar 1 coisa no mapa para salvar.'
                : `Faltam acertar ${quantasFaltas} coisas no mapa para salvar.`}{' '}
              Feche esta janela para continuar editando.
            </p>
          )}
          {salvar.isError && (
            <Faixa tom="bloqueado">
              {erroDosBlocos(salvar.error)
                ? 'O servidor achou o que acertar: feche esta janela para ver os blocos marcados no mapa.'
                : recadoDeErro(salvar.error, {
                    generica: 'Não foi possível salvar agora. Nada mudou; tente de novo.',
                    porCodigo: {
                      ENGINE_INDISPONIVEL:
                        'O serviço das automações não respondeu. Nada mudou; tente de novo daqui a pouco.',
                      CONFLITO_DE_VERSAO:
                        'Alguém salvou esta automação depois que você abriu. Descarte e abra de novo para ver a versão nova.',
                    },
                  })}
            </Faixa>
          )}
        </Dialogo>
      )}
    </div>
  );
}

/** O erro veio com problemas por bloco (os que o mapa marca). */
function erroDosBlocos(erro: unknown): boolean {
  return erro instanceof ErroDaApi && (erro.problema?.errors?.length ?? 0) > 0;
}

/** Quanto tempo o "Desfazer" fica na tela depois de tirar um bloco. */
const TEMPO_PARA_DESFAZER_MS = 8000;

/** Quanto o caminho simulado fica em cada bloco antes de seguir. */
const PASSO_DO_PERCURSO_MS = 700;
